import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Users, Send, TrendingUp, Wallet } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import RecentActivity from "@/components/RecentActivity";

/**
 * MoneyInMotion
 *
 * Live ticker + sparklines + recent-activity feed for the landing page.
 * Pulls anonymised aggregates from /api/stats/public every 10s.
 */
export default function MoneyInMotion() {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const fetchStats = async () => {
      try {
        const { data } = await api.get("/stats/public");
        if (cancelled) return;
        setStats(data);
        setPulse((p) => p + 1);
      } catch { /* silent */ }
      finally {
        if (!cancelled) timer = setTimeout(fetchStats, 10000);
      }
    };
    fetchStats();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const t = stats?.trends || {};

  return (
    <section className="border-y border-[#D2CDBE] bg-[#0A0A08] text-white relative overflow-hidden" data-testid="money-in-motion">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "linear-gradient(90deg, transparent 0%, transparent 99%, rgba(255,255,255,.4) 99%, rgba(255,255,255,.4) 100%)",
        backgroundSize: "40px 100%",
      }} />
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-12 relative">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B58957]" data-testid="money-in-motion-eyebrow">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B58957] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#B58957]" />
              </span>
              Live · Money in motion
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-3 leading-tight">
              Right now, on FinVault.
            </h2>
            <p className="text-white/60 mt-3 max-w-xl text-sm leading-relaxed">
              Anonymised, real-time aggregates from the last seven days. Updated every ten seconds — no personal data, no transaction details, just the rhythm of the wallet.
            </p>
          </div>
          <div className="text-xs text-white/50" data-testid="money-in-motion-asof">
            As of {stats?.as_of ? new Date(stats.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="money-in-motion-grid">
          <Tile Icon={TrendingUp} label="Moved this week" value={stats?.money_moved_week} trend={t.money_moved}
                format="currency" accent="text-[#B58957]" stroke="#B58957" pulseKey={pulse} testId="ticker-moved" />
          <Tile Icon={Send} label="Transfers" value={stats?.transfers_week} trend={t.transfers}
                format="int" accent="text-white" stroke="#FFFFFF" pulseKey={pulse} testId="ticker-transfers" />
          <Tile Icon={Users} label="New accounts" value={stats?.new_accounts_week} trend={t.new_accounts}
                format="int" accent="text-white" stroke="#FFFFFF" pulseKey={pulse} testId="ticker-new-accounts" />
          <Tile Icon={Activity} label="Active in 24h" value={stats?.active_users_24h} trend={t.active_users}
                format="int" accent="text-white" stroke="#FFFFFF" pulseKey={pulse} testId="ticker-active-24h" />
          <Tile Icon={Wallet} label="Avg deposit" value={stats?.avg_deposit_week} trend={t.avg_deposit}
                format="currency" accent="text-[#B58957]" stroke="#B58957" pulseKey={pulse} testId="ticker-avg-deposit" />
        </div>

        {/* Kinetic activity feed */}
        <RecentActivity />
      </div>
    </section>
  );
}

function Tile({ Icon, label, value, trend, format, accent, stroke, pulseKey, testId }) {
  const data = (trend && trend.length ? trend : new Array(7).fill(0)).map((v, i) => ({ d: i, v }));
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/10 p-5" data-testid={testId}>
      <div className="flex items-center justify-between text-white/60">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-[10px] uppercase tracking-[0.16em]">{label}</span>
      </div>
      <div className={`font-display text-3xl md:text-4xl font-bold mt-3 tabular-nums ${accent}`}>
        <CountUp value={value ?? 0} format={format} pulseKey={pulseKey} />
      </div>
      <div className="mt-3 h-10 -mx-1" data-testid={`${testId}-sparkline`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <YAxis hide domain={["dataMin", "dataMax + 1"]} />
            <Line
              type="monotone"
              dataKey="v"
              stroke={stroke}
              strokeWidth={1.6}
              dot={false}
              isAnimationActive={true}
              animationDuration={700}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 text-[10px] text-white/40 uppercase tracking-widest">7-day trend</div>
    </div>
  );
}

function CountUp({ value, format = "int", pulseKey = 0, durationMs = 900 }) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const start = prevRef.current;
    const end = Number(value) || 0;
    if (start === end) { setDisplay(end); return; }
    const startTs = performance.now();
    let raf;
    const tick = (t) => {
      const elapsed = t - startTs;
      const k = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(start + (end - start) * eased);
      if (k < 1) raf = requestAnimationFrame(tick);
      else prevRef.current = end;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, pulseKey]);

  if (format === "currency") {
    return <>{"$"}{Math.round(display).toLocaleString()}</>;
  }
  return <>{Math.round(display).toLocaleString()}</>;
}
